import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';

import {
  getEmailValidation,
  getMagicLink,
  setEmailValidation as updateEmailValidation,
  setMagicLink as updateMagicLink
} from '../../../api';
import { patchFineConfigurations } from '../../../api/configurations';
import {
  EmailValidations,
  MagicLink as MagicLinkType
} from '../../../api/configurations/emailSettings/types';
import { Error } from '../../../api/setup/getApiError';
import { CLASSIC_LOGIN, PASSWORDLESS_LOGIN } from '../../../constants';
import { useConfig } from '../../../contexts/config';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { VerticalHorizontalCenterBlock } from '../../../styled';
import { FormFooter, HeaderTitle, Preloader, ToNavigationError } from '../../ui';
import EmailValidation from './EmailValidation';
import MagicLink from './MagicLink';
import { Props, Values } from './types';

const EmailSettings = ({ currentProjectType, currentProject, history }: Props): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const { xsollaAnalytics } = useConfig();
  const [emailSettingsFetching, setEmailSettingsFetching] = useState<boolean>(true);
  const [submitFetching, setSubmitFetching] = useState<boolean>(false);
  const [emailValidation, setEmailValidation] = useState<EmailValidations>({
    enable_smtp: false,
    enable_rfc: false,
    enable_graylist: false
  });
  const [magicLink, setMagicLink] = useState<boolean>(false);

  const [getDataError, setGetDataError] = useState<Error | null>(null);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  useEffect(() => {
    setEmailSettingsFetching(true);
    const requestArr: [Promise<EmailValidations>, Promise<MagicLinkType>] = [
      getEmailValidation(currentProject.id),
      currentProjectType === PASSWORDLESS_LOGIN
        ? getMagicLink(currentProject.id)
        : new Promise((resolve) => resolve({ enable_magic_link: false }))
    ];

    Promise.all([...requestArr])
      .then(([validationResp, magicLinkResp]) => {
        setEmailValidation(validationResp);
        setMagicLink(magicLinkResp.enable_magic_link);
      })
      .catch((err) => setGetDataError(err))
      .finally(() => setEmailSettingsFetching(false));
  }, []);

  const backToNavigation = () => history.push({ pathname: navigationPath(currentProject.id) });

  const onSubmit = (values: Values) => {
    setSubmitFetching(true);

    const { enable_magic_link, ...emailValues } = values;

    xsollaAnalytics.sendClick('login_pa_module__email_settings__magic_link', 'submit', {
      enable_magic_link
    });
    xsollaAnalytics.sendClick('login_pa_module__email_settings__email_values', 'submit', {
      ...emailValues
    });

    const emailConfirmationBody = {
      email_confirmation_needed: values.email_confirmation_needed
    };

    const requests: [Promise<boolean>, Promise<EmailValidations>, Promise<MagicLinkType>] = [
      patchFineConfigurations(emailConfirmationBody, currentProject.id),
      updateEmailValidation(currentProject.id, emailValues),
      currentProjectType === PASSWORDLESS_LOGIN
        ? updateMagicLink(currentProject.id, { enable_magic_link })
        : new Promise((resolve) => resolve({ enable_magic_link: false }))
    ];

    Promise.all(requests)
      .then(() => {
        showSuccessNotify();
      })
      .catch(([errConfirmation, errEmail, errMagicLink]) => {
        showErrorNotify(errConfirmation);
        showErrorNotify(errEmail);
        showErrorNotify(errMagicLink);
        setSubmitError(true);
      })
      .finally(() => {
        setSubmitFetching(false);
      });
  };

  const renderTitle = useMemo(() => {
    if (currentProjectType === CLASSIC_LOGIN) {
      return (
        <HeaderTitle
          title={intl.formatMessage({ id: 'settings.email-validation.title' })}
          subtitle={intl.formatMessage({ id: 'settings.email-validation.subtitle' })}
        />
      );
    }

    return (
      <HeaderTitle
        title={intl.formatMessage({ id: 'settings.email-settings.title' })}
        subtitle={intl.formatMessage({ id: 'settings.email-settings.subtitle' })}
      />
    );
  }, [currentProjectType]);

  if (emailSettingsFetching) {
    return (
      <VerticalHorizontalCenterBlock>
        <Preloader />
      </VerticalHorizontalCenterBlock>
    );
  }

  if (getDataError) {
    return <ToNavigationError error={getDataError} pageName="email-settings_open" />;
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        enable_magic_link: magicLink,
        enable_smtp: emailValidation.enable_smtp,
        enable_rfc: emailValidation.enable_rfc,
        enable_graylist: emailValidation.enable_graylist,
        email_confirmation_needed: currentProject.email_confirmation_needed
      }}>
      {({ handleSubmit }) => {
        return (
          <>
            {renderTitle}

            {currentProjectType === PASSWORDLESS_LOGIN && <MagicLink />}

            <EmailValidation currentProjectType={currentProjectType} />

            <FormFooter
              onSubmit={handleSubmit}
              onCancel={backToNavigation}
              fetching={submitFetching}
              error={submitError}
            />
          </>
        );
      }}
    </Form>
  );
};

export default withRouter(EmailSettings);
